import { Collection } from '../hooks/useCollections';
import { useTranslation } from 'react-i18next';

interface CollectionSidebarProps {
  collections: Collection[];
  selectedCollectionId: string | null;
  uncategorizedCount: number;
  totalCount: number;
  onSelectCollection: (id: string | null) => void;
  onCreateCollection: () => void;
  onEditCollection?: (collection: Collection) => void;
  onDeleteCollection?: (id: string) => void;
  isLoading?: boolean;
}

export default function CollectionSidebar({
  collections,
  selectedCollectionId,
  uncategorizedCount,
  totalCount,
  onSelectCollection,
  onCreateCollection,
  onEditCollection,
  onDeleteCollection,
  isLoading = false,
}: CollectionSidebarProps) {
  const collectionsTotal = collections.reduce((sum, c) => sum + (c.bookmarkCount || 0), 0);
  const computedUncategorized = totalCount - collectionsTotal;
  const { t } = useTranslation();

  return (
    <div className="w-64 flex-shrink-0 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 h-fit">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wide">
          {t('collectionSidebar.title')}
        </h2>
        <button
          onClick={onCreateCollection}
          className="p-1.5 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          title={t('collectionSidebar.newCollection')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <svg className="w-5 h-5 text-slate-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : (
        <nav className="space-y-1">
          {/* All bookmarks */}
          <button
            onClick={() => onSelectCollection(null)}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
              selectedCollectionId === null
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <span>{t('collectionSidebar.allBookmarks')}</span>
            </div>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              {totalCount}
            </span>
          </button>

          {/* Uncategorized */}
          <button
            onClick={() => onSelectCollection('uncategorized')}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
              selectedCollectionId === 'uncategorized'
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>{t('collectionSidebar.uncategorized')}</span>
            </div>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              {uncategorizedCount > 0 ? uncategorizedCount : computedUncategorized}
            </span>
          </button>

          {/* Divider */}
          {collections.length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 my-2" />
          )}

          {/* User collections */}
          {collections.map((collection) => (
            <div key={collection.id} className="group">
              <button
                onClick={() => onSelectCollection(collection.id)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                  selectedCollectionId === collection.id
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <span className="truncate">{collection.name}</span>
                  {collection.isPublic && (
                    <svg className="w-3 h-3 flex-shrink-0 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                    </svg>
                  )}
                </div>
                {/* Fixed width container for count/actions to prevent size jump */}
                <div className="w-12 flex justify-end">
                  {/* Show count by default, hide on hover when actions are available */}
                  <span className={`text-xs font-medium text-slate-500 dark:text-slate-400 ${(onEditCollection || onDeleteCollection) ? 'group-hover:hidden' : ''}`}>
                    {collection.bookmarkCount || 0}
                  </span>
                  {/* Action buttons - shown on hover */}
                  {(onEditCollection || onDeleteCollection) && (
                    <div className="hidden group-hover:flex items-center">
                      {onEditCollection && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditCollection(collection);
                          }}
                          className="p-1 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 rounded cursor-pointer"
                          title={t('collectionSidebar.edit')}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </span>
                      )}
                      {onDeleteCollection && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(t('collectionSidebar.confirmDelete'))) {
                              onDeleteCollection(collection.id);
                            }
                          }}
                          className="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded cursor-pointer"
                          title={t('collectionSidebar.delete')}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            </div>
          ))}
        </nav>
      )}
    </div>
  );
}
